import React, { useEffect, useMemo, useState, useRef } from 'react';
import './AgentConfigurator.css';
import {
  DEFAULT_FINAL_STEP_PROMPT,
  normalizeLegacyRagDocIds,
  normalizeReminderPromptPosition,
  type AgentConfiguratorState,
  type AgentCoordinatorState,
  type AgentWorker,
  type ReminderPromptPosition,
} from '../../types/agentConfigurator';
import { normalizeOptimizationApproach, type OptimizationApproach } from '../../types/optimizationApproach';
import {
  getGpt5ModelSpec,
  getTemperatureInactiveHint,
  isBaseModelUnsupportedForFineTuning,
  reasoningOptionLabel,
  resolveModelsReasoning,
  shouldShowTemperature,
} from '../../utils/gpt5ConfiguratorModels';
import {
  MAX_AGENT_DESCRIPTION_LENGTH,
  MAX_AGENT_PROMPT_LENGTH,
  MAX_COORDINATOR_PROMPT_LENGTH,
} from '../../constants/agentFieldLimits';
import { FieldCharCount } from '../ui/FieldCharCount';
import { NOTIFICATION_AUTO_HIDE_MS } from '../../constants/uiTiming';
import {
  defaultNltAdminToolExtras,
  isNltAdminToolEnabled,
  NLT_ADMIN_AGENT_TOOL_FIELDS,
  NLT_ADMIN_AGENT_TOOL_UI,
  NLT_ADMIN_TOOLS_SNAPSHOT_KEY,
  normalizeNltAdminToolsFromInput,
  applyNltAdminToolToggle,
  applyNltAdminToolsSnapshot,
  readNltAdminToolsSnapshot,
  toggleNltAdminTool,
  type NltAdminAgentToolField,
} from '../../constants/nltAdminAgentTools';
import { getAgentNameUniquenessError, getDuplicateAgentNameKeys } from '../../utils/agentNameUniqueness';
import { formatConfiguratorModelLabel, mergeConfiguratorModelOptions } from '../../utils/configuratorModels';
import AcCollapsibleSection, { type AcConfiguratorSectionId } from './AcCollapsibleSection';
import AcFieldPatchDiff from './AcFieldPatchDiff';
import {
  buildConfigFieldDiffs,
  getFieldDiffMap,
  workerPathKey,
  type ConfigFieldDiff,
  type FieldDecision,
} from '../../utils/nltConfigFieldDiff';
import type { AgentConfiguratorPatchReview } from '../../types/nltPatchReview';

const MAX_NAME_LENGTH = 100;
const MAX_AGENT_BACKEND_ID_LENGTH = 128;
const MAX_CONFIG_LABEL_LENGTH = 100;
// Validation: keep it permissive.
// We already cap length separately (MAX_NAME_LENGTH), so avoid stripping non-ascii characters from history.
const AGENT_NAME_REGEX = /^.+$/;
const MAX_TEMPERATURE = 1.0;
const DEFAULT_CONFIG_LABEL = 'Untitled Configuration';
const MAX_CORE_PROMPT_LENGTH = 6000;
const MAX_META_PROMPT_LENGTH = 2000;

function resolveTrainableFlagForModelChange(
  prevModel: string,
  nextModel: string,
  currentValue: boolean | undefined
): boolean {
  if (isBaseModelUnsupportedForFineTuning(nextModel)) return false;
  if (currentValue === false && isBaseModelUnsupportedForFineTuning(prevModel)) return true;
  return currentValue !== false;
}

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function sanitizeAgentName(value: string): string {
  // Keep human-readable names as-is (only normalize whitespace and cap length).
  return value.replace(/\s+/g, ' ').trim().slice(0, MAX_NAME_LENGTH);
}

function sanitizeConfigLabel(value: string): string {
  return value.slice(0, MAX_CONFIG_LABEL_LENGTH);
}

function normalizeTemperature(temp: number | undefined): number {
  if (typeof temp !== 'number') return 0.7;
  return temp > MAX_TEMPERATURE ? MAX_TEMPERATURE : temp;
}

function normalizeModel(model: string | undefined): string {
  const m = String(model ?? '').trim();
  return m || 'gpt-4.1-mini';
}

function createDefaultCoordinator(nltAdminMode = false): AgentCoordinatorState {
  return {
    name: 'AgentCoordinator',
    description: 'Coordinator node that communicates with worker nodes to respond to user messages.',
    prompt: "You are the coordinator of workers. Use them to respond to the user's message.",
    model: 'gpt-4.1-mini',
    temperature: 0.8,
    ...(nltAdminMode
      ? defaultNltAdminToolExtras(true)
      : { use_rag: false, use_ltm: false, is_trainable: true }),
  };
}

function createDefaultWorkers(nltAdminMode = false): AgentWorker[] {
  const agentExtras = nltAdminMode
    ? defaultNltAdminToolExtras(true)
    : { use_rag: false, use_ltm: false, is_trainable: true };
  return [
    {
      id: uuid(),
      name: 'synthesizer123',
      description: 'Synthesizer - summarizes messages to answer the initial user message.',
      prompt: 'You are a synthesizer. Review entire message history and produce a final reply...',
      mode: 'synchronous',
      model: 'gpt-4.1-mini',
      temperature: 0.7,
      ...agentExtras,
    },
    {
      id: uuid(),
      name: 'expert123',
      description: 'Expert - provides an expert opinion once and then stays silent.',
      prompt: 'You are a client support expert. Provide multiple possible solutions...',
      mode: 'asynchronous',
      model: 'gpt-4.1-mini',
      temperature: 0.7,
      ...agentExtras,
    },
  ];
}

function createDefaultMetaPrompts(): Record<string, string> {
  return {
    CORE_PROMPT: '',
    MASTER_SUFFIX: '',
    ASYNC_SUFFIX: '',
    SYNC_EXTRA: '',
    RAG_PROMPT: '',
    LTM_PROMPT: '',
    MODERATION_PROMPT: '',
    DIARY_WRITE_PROMPT: '',
    DIARY_READ_PROMPT: '',
    PORTRAIT_READ_PROMPT: '',
    PORTRAIT_WRITE_PROMPT: '',
    FINAL_STEP_PROMPT: DEFAULT_FINAL_STEP_PROMPT,
  };
}

function sanitizeMetaPrompts(
  source?: Record<string, unknown> | null
): Record<string, string> {
  const base = createDefaultMetaPrompts();
  const merged: Record<string, string> = { ...base };
  if (!source || typeof source !== 'object') return merged;
  Object.entries(source).forEach(([key, value]) => {
    if (typeof value === 'string') {
      merged[key] = value;
    }
  });
  return merged;
}

function normalizeIncomingConfig(
  input: AgentConfiguratorState | null | undefined,
  opts?: { nltAdminMode?: boolean }
): AgentConfiguratorState {
  const nltAdminMode = Boolean(opts?.nltAdminMode);
  const fallbackMeta = createDefaultMetaPrompts();
  const coordinator = input?.coordinator
    ? (() => {
        const cm = normalizeModel(input.coordinator.model);
        const cr =
          (input.coordinator as { models_reasoning?: string; model_reasoning?: string }).models_reasoning ??
          (input.coordinator as { model_reasoning?: string }).model_reasoning;
        return {
          name: sanitizeAgentName(String(input.coordinator.name || 'AgentCoordinator')),
          description: String(input.coordinator.description || '').slice(0, MAX_AGENT_DESCRIPTION_LENGTH),
          prompt: String(input.coordinator.prompt || '').slice(0, MAX_COORDINATOR_PROMPT_LENGTH),
          model: cm,
          temperature: normalizeTemperature(input.coordinator.temperature),
          models_reasoning: resolveModelsReasoning(cm, cr),
          use_rag: Boolean(input.coordinator.use_rag),
          use_ltm: Boolean(input.coordinator.use_ltm),
          use_diary_read: Boolean(input.coordinator.use_diary_read),
          use_diary_write: Boolean(input.coordinator.use_diary_write),
          use_portrait_read: Boolean(input.coordinator.use_portrait_read),
          use_portrait_write: Boolean(input.coordinator.use_portrait_write),
          is_trainable: typeof input.coordinator.is_trainable === 'boolean' ? input.coordinator.is_trainable : true,
          agent_id: input.coordinator.agent_id,
          id: input.coordinator.id,
          reminder_prompt:
            typeof input.coordinator.reminder_prompt === 'string'
              ? String(input.coordinator.reminder_prompt).slice(0, MAX_AGENT_PROMPT_LENGTH)
              : undefined,
          rag_doc_ids: normalizeLegacyRagDocIds(
            (input.coordinator as { rag_doc_ids?: unknown }).rag_doc_ids
          ),
          ...normalizeNltAdminToolsFromInput(
            input.coordinator as Record<string, unknown>,
            nltAdminMode,
            defaultNltAdminToolExtras(false)
          ),
        };
      })()
    : createDefaultCoordinator(nltAdminMode);

  const workers = Array.isArray(input?.workers)
    ? input!.workers.map((w) => {
        const wm = normalizeModel(w.model);
        const wr = (w as { models_reasoning?: string; model_reasoning?: string }).models_reasoning ??
          (w as { model_reasoning?: string }).model_reasoning;
        return {
          id: w.id || uuid(),
          name: sanitizeAgentName(String(w.name || 'Agent')),
          description: String(w.description || '').slice(0, MAX_AGENT_DESCRIPTION_LENGTH),
          prompt: String(w.prompt || '').slice(0, MAX_AGENT_PROMPT_LENGTH),
          mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as AgentWorker['mode'],
          model: wm,
          temperature: normalizeTemperature(w.temperature),
          models_reasoning: resolveModelsReasoning(wm, wr),
          use_rag: Boolean(w.use_rag),
          use_ltm: Boolean(w.use_ltm),
          use_diary_read: Boolean(w.use_diary_read),
          use_diary_write: Boolean(w.use_diary_write),
          use_portrait_read: Boolean(w.use_portrait_read),
          use_portrait_write: Boolean(w.use_portrait_write),
          is_trainable: typeof w.is_trainable === 'boolean' ? w.is_trainable : true,
          reminder_prompt:
            typeof w.reminder_prompt === 'string'
              ? String(w.reminder_prompt).slice(0, MAX_AGENT_PROMPT_LENGTH)
              : undefined,
          rag_doc_ids: normalizeLegacyRagDocIds((w as { rag_doc_ids?: unknown }).rag_doc_ids),
          ...normalizeNltAdminToolsFromInput(
            w as Record<string, unknown>,
            nltAdminMode,
            defaultNltAdminToolExtras(true)
          ),
        };
      })
    : createDefaultWorkers(nltAdminMode);

  const metaPrompts = sanitizeMetaPrompts({
    ...fallbackMeta,
    ...(input?.metaPrompts || {}),
  });

  const approach = normalizeOptimizationApproach(input?.approach);
  const async_orchestrator =
    input?.async_orchestrator && typeof input.async_orchestrator === 'object'
      ? { ...input.async_orchestrator }
      : undefined;

  let result: AgentConfiguratorState = {
    approach,
    coordinator,
    workers,
    metaPrompts,
    async_orchestrator,
    reminder_prompt_position: normalizeReminderPromptPosition(input?.reminder_prompt_position),
  };

  if (nltAdminMode) {
    const snapshot = readNltAdminToolsSnapshot(
      (input as Record<string, unknown> | null | undefined)?.[NLT_ADMIN_TOOLS_SNAPSHOT_KEY]
    );
    result = applyNltAdminToolsSnapshot(result, snapshot);
  }

  return result;
}

export interface AgentConfiguratorProps {
  initialConfig?: AgentConfiguratorState | null;
  initialLabel?: string;
  onSave: (config: AgentConfiguratorState, label?: string) => Promise<void> | void;
  onCancel?: () => void;
  onConfigChange?: (config: AgentConfiguratorState) => void;
  /** Full parsed JSON from Import — parent can keep fields the UI does not edit and merge them on Save. */
  onImportRawConfig?: (raw: Record<string, unknown>) => void;
  /** When true, Approach selector is not rendered (e.g. when parent renders it next to Optimize) */
  hideApproach?: boolean;
  /** When true, Reminder position is not rendered here (e.g. Connector shows it in the Optimize row). */
  hideReminderPromptPosition?: boolean;
  /** Models to show in coordinator/worker model dropdowns (from Profile → Models). If not set, default list is used. */
  availableModels?: string[];
  /** Readable labels for model ids (e.g. fine-tuned `ft:…` id → friendly name). */
  modelLabels?: Record<string, string>;
  /** Admin NLT config: hide connector tools/trainable flags; show use_config_patch_tool per agent */
  nltAdminMode?: boolean;
  /** NLT: inline field diffs (old red / new green) next to agent fields */
  patchReview?: AgentConfiguratorPatchReview | null;
}

export const AgentConfigurator: React.FC<AgentConfiguratorProps> = ({
  initialConfig,
  initialLabel,
  onSave,
  onCancel,
  onConfigChange,
  onImportRawConfig,
  hideApproach = false,
  hideReminderPromptPosition = false,
  availableModels: availableModelsProp,
  modelLabels,
  nltAdminMode = false,
  patchReview = null,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [configLabel, setConfigLabel] = useState<string>(sanitizeConfigLabel(initialLabel || ''));
  const [bulkModel, setBulkModel] = useState<string>('gpt-4.1-mini');
  const [approach, setApproach] = useState<OptimizationApproach>('BASELINE');
  const [coordinator, setCoordinator] = useState<AgentCoordinatorState>(() =>
    createDefaultCoordinator(nltAdminMode)
  );
  const [workers, setWorkers] = useState<AgentWorker[]>(() => createDefaultWorkers(nltAdminMode));

  const availableModels = useMemo(
    () =>
      mergeConfiguratorModelOptions([
        ...(availableModelsProp ?? []),
        coordinator.model,
        ...workers.map((worker) => worker.model),
      ]),
    [availableModelsProp, coordinator.model, workers]
  );
  const [metaPrompts, setMetaPrompts] = useState<Record<string, string>>(() => createDefaultMetaPrompts());
  const [reminderPromptPosition, setReminderPromptPosition] = useState<ReminderPromptPosition>('before');

  const [lastSavedConfig, setLastSavedConfig] = useState<{
    coordinator: AgentCoordinatorState;
    workers: AgentWorker[];
    label: string;
    metaPrompts: Record<string, string>;
    approach?: OptimizationApproach;
    async_orchestrator?: AgentConfiguratorState['async_orchestrator'];
    reminder_prompt_position?: ReminderPromptPosition;
  } | null>(null);

  const [sectionCollapsed, setSectionCollapsed] = useState<Partial<Record<AcConfiguratorSectionId, boolean>>>({});
  const isSectionCollapsed = (key: AcConfiguratorSectionId) => Boolean(sectionCollapsed[key]);
  const expandSection = (key: AcConfiguratorSectionId) => {
    setSectionCollapsed((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  };
  const toggleSection = (key: AcConfiguratorSectionId) => {
    setSectionCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const syncWorkers = useMemo(() => workers.filter((w) => w.mode === 'synchronous'), [workers]);
  const asyncWorkers = useMemo(() => workers.filter((w) => w.mode === 'asynchronous'), [workers]);
  const duplicateAgentNameKeys = useMemo(
    () => getDuplicateAgentNameKeys(coordinator.name, workers),
    [coordinator.name, workers]
  );
  const isCoordinatorNameDuplicated = duplicateAgentNameKeys.has(String(coordinator.name || '').trim().toLowerCase());

  const patchFieldDiffMap = useMemo(() => {
    if (!patchReview) return {} as Record<string, ConfigFieldDiff>;
    return getFieldDiffMap(
      buildConfigFieldDiffs(patchReview.baseConfig, patchReview.proposedConfig)
    );
  }, [patchReview]);

  const patchReviewActive = Boolean(patchReview);
  const isPatchFieldLocked = (path: string) =>
    patchReviewActive && Boolean(patchFieldDiffMap[path]);

  useEffect(() => {
    const path = patchReview?.focusedPatchPath;
    if (!path) return;

    if (path.startsWith('metaPrompts.')) {
      expandSection('metaPrompts');
    } else if (path.startsWith('coordinator.')) {
      expandSection('coordinator');
    } else if (path.startsWith('workers.')) {
      setSectionCollapsed((prev) => ({
        ...prev,
        syncWorkers: false,
        asyncWorkers: false,
      }));
    }

    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-nlt-patch-path="${CSS.escape(path)}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [patchReview?.focusedPatchPath]);

  useEffect(() => {
    if (!patchReview) return;
    const hasMetaPromptDiffs = Object.keys(patchFieldDiffMap).some((p) => p.startsWith('metaPrompts.'));
    if (hasMetaPromptDiffs) {
      expandSection('metaPrompts');
    }
  }, [patchReview, patchFieldDiffMap]);

  const coordinatorToolDiffs = useMemo(
    () =>
      Object.values(patchFieldDiffMap).filter(
        (diff) =>
          diff.path.startsWith('coordinator.use_') ||
          diff.path === 'coordinator.use_config_patch_tool'
      ),
    [patchFieldDiffMap]
  );

  useEffect(() => {
    if (!bulkModel || !availableModels.includes(bulkModel)) {
      setBulkModel(coordinator.model || availableModels[0] || 'gpt-4.1-mini');
    }
  }, [bulkModel, coordinator.model, availableModels]);

  // Apply incoming config (e.g. when selecting another integration)
  // Используем ref для отслеживания, был ли label только что сохранен
  const justSavedLabelRef = useRef<string | null>(null);
  const prevInitialLabelRef = useRef<string | undefined>(initialLabel);
  
  useEffect(() => {
    const normalized = normalizeIncomingConfig(initialConfig, { nltAdminMode });
    setApproach(normalized.approach || 'BASELINE');
    setCoordinator(normalized.coordinator);
    setWorkers(normalized.workers);
    setMetaPrompts(sanitizeMetaPrompts(normalized.metaPrompts as Record<string, unknown>));
    setReminderPromptPosition(normalizeReminderPromptPosition(normalized.reminder_prompt_position));
    
    const label = sanitizeConfigLabel(initialLabel || '');
    
    // Обновляем configLabel только если initialLabel действительно изменился
    // и это не только что сохраненное значение
    const initialLabelChanged = prevInitialLabelRef.current !== initialLabel;
    const isJustSaved = justSavedLabelRef.current === label;
    
    if (initialLabelChanged && !isJustSaved) {
      setConfigLabel(label);
    }
    
    // Сбрасываем флаг после обработки
    if (isJustSaved) {
      justSavedLabelRef.current = null;
    }
    
    prevInitialLabelRef.current = initialLabel;
    
    setLastSavedConfig({
      coordinator: normalized.coordinator,
      workers: normalized.workers,
      label,
      metaPrompts: sanitizeMetaPrompts(normalized.metaPrompts as Record<string, unknown>),
      approach: normalized.approach || 'BASELINE',
      async_orchestrator: normalized.async_orchestrator ? { ...normalized.async_orchestrator } : undefined,
      reminder_prompt_position: normalizeReminderPromptPosition(normalized.reminder_prompt_position),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig, initialLabel]);

  // Уведомляем об изменениях конфигурации (только при реальных изменениях пользователя)
  const prevConfigRef = useRef<string>('');
  useEffect(() => {
    if (onConfigChange) {
      const currentConfig: AgentConfiguratorState = {
        approach,
        coordinator,
        workers,
        metaPrompts,
        async_orchestrator: initialConfig?.async_orchestrator ? { ...initialConfig.async_orchestrator } : undefined,
        reminder_prompt_position: reminderPromptPosition,
      };
      const configStr = JSON.stringify(currentConfig);
      // Вызываем callback только если конфигурация действительно изменилась
      if (configStr !== prevConfigRef.current) {
        prevConfigRef.current = configStr;
        onConfigChange(currentConfig);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approach, coordinator, workers, metaPrompts, reminderPromptPosition]);

  const handleReset = () => {
    if (lastSavedConfig) {
      setApproach((lastSavedConfig as any).approach || 'BASELINE');
      setCoordinator({ ...lastSavedConfig.coordinator });
      setWorkers(lastSavedConfig.workers.map((w) => ({ ...w })));
      setConfigLabel(lastSavedConfig.label);
      setMetaPrompts({ ...lastSavedConfig.metaPrompts });
      setReminderPromptPosition(
        normalizeReminderPromptPosition((lastSavedConfig as any).reminder_prompt_position)
      );
      return;
    }
    setApproach('BASELINE');
    setCoordinator(createDefaultCoordinator(nltAdminMode));
    setWorkers(createDefaultWorkers(nltAdminMode));
    setConfigLabel(DEFAULT_CONFIG_LABEL);
    setMetaPrompts(createDefaultMetaPrompts());
    setReminderPromptPosition('before');
  };

  const validate = (): string | null => {
    const errors: string[] = [];
    if (!coordinator.name || coordinator.name.length > MAX_NAME_LENGTH || !AGENT_NAME_REGEX.test(coordinator.name)) {
      errors.push('Coordinator name must be 1-100 English letters or digits.');
    }
    if (coordinator.id && coordinator.id.length > MAX_AGENT_BACKEND_ID_LENGTH) {
      errors.push(`Coordinator backend id must be at most ${MAX_AGENT_BACKEND_ID_LENGTH} characters.`);
    }
    if (coordinator.agent_id && coordinator.agent_id.length > MAX_AGENT_BACKEND_ID_LENGTH) {
      errors.push(`Coordinator agent_id must be at most ${MAX_AGENT_BACKEND_ID_LENGTH} characters.`);
    }
    if (coordinator.description && coordinator.description.length > MAX_AGENT_DESCRIPTION_LENGTH) {
      errors.push(`Coordinator description must be at most ${MAX_AGENT_DESCRIPTION_LENGTH} characters.`);
    }
    if (coordinator.prompt && coordinator.prompt.length > MAX_COORDINATOR_PROMPT_LENGTH) {
      errors.push(`Coordinator prompt must be at most ${MAX_COORDINATOR_PROMPT_LENGTH} characters.`);
    }
    if (coordinator.reminder_prompt && coordinator.reminder_prompt.length > MAX_AGENT_PROMPT_LENGTH) {
      errors.push(`Coordinator reminder prompt must be at most ${MAX_AGENT_PROMPT_LENGTH} characters.`);
    }
    workers.forEach((w, idx) => {
      if (!w.name || w.name.length > MAX_NAME_LENGTH || !AGENT_NAME_REGEX.test(w.name)) {
        errors.push(`Worker #${idx + 1} name must be 1-100 English letters or digits.`);
      }
      if (w.description && w.description.length > MAX_AGENT_DESCRIPTION_LENGTH) {
        errors.push(`Worker #${idx + 1} description must be at most ${MAX_AGENT_DESCRIPTION_LENGTH} characters.`);
      }
      if (w.prompt && w.prompt.length > MAX_AGENT_PROMPT_LENGTH) {
        errors.push(`Worker #${idx + 1} prompt must be at most ${MAX_AGENT_PROMPT_LENGTH} characters.`);
      }
      if (w.reminder_prompt && w.reminder_prompt.length > MAX_AGENT_PROMPT_LENGTH) {
        errors.push(`Worker #${idx + 1} reminder prompt must be at most ${MAX_AGENT_PROMPT_LENGTH} characters.`);
      }
    });

    const nameUniquenessError = getAgentNameUniquenessError(coordinator.name, workers);
    if (nameUniquenessError) errors.push(nameUniquenessError);

    if (metaPrompts.CORE_PROMPT && metaPrompts.CORE_PROMPT.length > MAX_CORE_PROMPT_LENGTH) {
      errors.push(`CORE_PROMPT must be at most ${MAX_CORE_PROMPT_LENGTH} characters.`);
    }
    (
      [
        'MASTER_SUFFIX',
        'ASYNC_SUFFIX',
        'SYNC_EXTRA',
        'RAG_PROMPT',
        'LTM_PROMPT',
        'MODERATION_PROMPT',
        'DIARY_WRITE_PROMPT',
        'DIARY_READ_PROMPT',
        'PORTRAIT_READ_PROMPT',
        'PORTRAIT_WRITE_PROMPT',
        'FINAL_STEP_PROMPT',
      ] as const
    ).forEach((key) => {
      const v = metaPrompts[key];
      if (v && v.length > MAX_META_PROMPT_LENGTH) {
        errors.push(`${key} must be at most ${MAX_META_PROMPT_LENGTH} characters.`);
      }
    });

    return errors[0] || null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setValidationError(err);
      setShowSuccessMessage(false);
      return;
    }
    setValidationError(null);
    setShowSuccessMessage(false);

    const normalizedLabelForSave = sanitizeConfigLabel(configLabel).trim();
    const metaPromptsToSave = sanitizeMetaPrompts(metaPrompts as Record<string, unknown>);

    const cfg: AgentConfiguratorState = {
      approach,
      coordinator,
      workers,
      metaPrompts: metaPromptsToSave,
      async_orchestrator: initialConfig?.async_orchestrator ? { ...initialConfig.async_orchestrator } : undefined,
      reminder_prompt_position: reminderPromptPosition,
    };

    setIsSaving(true);
    try {
      // Передаем label только если он не пустой, иначе undefined
      const labelToSave = normalizedLabelForSave || undefined;
      await onSave(cfg, labelToSave);
      setLastSavedConfig({
        coordinator: { ...coordinator },
        workers: workers.map((w) => ({ ...w })),
        label: normalizedLabelForSave, // Сохраняем как есть (может быть пустой строкой)
        metaPrompts: metaPromptsToSave,
        approach,
        async_orchestrator: cfg.async_orchestrator ? { ...cfg.async_orchestrator } : undefined,
        reminder_prompt_position: reminderPromptPosition,
      } as any);
      // Сохраняем нормализованный label (без лишних пробелов)
      // Если пользователь ввел значение, сохраняем его нормализованную версию
      setConfigLabel(normalizedLabelForSave);
      // Устанавливаем флаг, что label был только что сохранен, чтобы useEffect не сбросил его
      justSavedLabelRef.current = normalizedLabelForSave;
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), NOTIFICATION_AUTO_HIDE_MS);
    } catch (e: any) {
      setValidationError(e?.message || 'Failed to save configuration');
      setShowSuccessMessage(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportConfig = () => {
    const configToExport = {
      label: configLabel.trim() || DEFAULT_CONFIG_LABEL,
      approach,
      coordinator,
      workers,
      metaPrompts: sanitizeMetaPrompts(metaPrompts as Record<string, unknown>),
      async_orchestrator: initialConfig?.async_orchestrator ? { ...initialConfig.async_orchestrator } : undefined,
      reminder_prompt_position: reminderPromptPosition,
    };
    const jsonString = JSON.stringify(configToExport, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `agent-config-${configLabel.trim() || 'export'}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImportConfig = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target?.result as string);

          if (json && typeof json === 'object' && json.coordinator && Array.isArray(json.workers)) {
            onImportRawConfig?.(json as Record<string, unknown>);
          }

          if (json.approach != null) {
            setApproach(normalizeOptimizationApproach(json.approach));
          } else {
            setApproach('BASELINE');
          }

          setReminderPromptPosition(normalizeReminderPromptPosition(json.reminder_prompt_position));

          if (json.coordinator) {
            const cm = normalizeModel(json.coordinator.model);
            const cr = json.coordinator.models_reasoning ?? json.coordinator.model_reasoning;
            setCoordinator({
              name: sanitizeAgentName(String(json.coordinator.name || 'AgentCoordinator')),
              description: String(json.coordinator.description || '').slice(0, MAX_AGENT_DESCRIPTION_LENGTH),
              prompt: String(json.coordinator.prompt || '').slice(0, MAX_COORDINATOR_PROMPT_LENGTH),
              model: cm,
              temperature: normalizeTemperature(json.coordinator.temperature),
              models_reasoning: resolveModelsReasoning(cm, cr),
              use_rag: json.coordinator.use_rag === true,
              use_ltm: json.coordinator.use_ltm === true,
              use_diary_read: Boolean(json.coordinator.use_diary_read),
              use_diary_write: Boolean(json.coordinator.use_diary_write),
              use_portrait_read: Boolean(json.coordinator.use_portrait_read),
              use_portrait_write: Boolean(json.coordinator.use_portrait_write),
              is_trainable:
                typeof json.coordinator.is_trainable === 'boolean' ? json.coordinator.is_trainable : true,
              id:
                typeof json.coordinator.id === 'string'
                  ? json.coordinator.id.slice(0, MAX_AGENT_BACKEND_ID_LENGTH)
                  : undefined,
              agent_id:
                typeof json.coordinator.agent_id === 'string'
                  ? json.coordinator.agent_id.slice(0, MAX_AGENT_BACKEND_ID_LENGTH)
                  : undefined,
              reminder_prompt:
                typeof json.coordinator.reminder_prompt === 'string'
                  ? String(json.coordinator.reminder_prompt).slice(0, MAX_AGENT_PROMPT_LENGTH)
                  : undefined,
            });
          }

          if (Array.isArray(json.workers)) {
            setWorkers(
              json.workers.map((w: any) => {
                const wm = normalizeModel(w.model);
                const wr = w.models_reasoning ?? w.model_reasoning;
                return {
                  id: w.id || uuid(),
                  name: sanitizeAgentName(String(w.name || 'Agent')),
                  description: String(w.description || '').slice(0, MAX_AGENT_DESCRIPTION_LENGTH),
                  prompt: String(w.prompt || '').slice(0, MAX_AGENT_PROMPT_LENGTH),
                  mode: (w.mode === 'asynchronous' ? 'asynchronous' : 'synchronous') as AgentWorker['mode'],
                  model: wm,
                  temperature: normalizeTemperature(w.temperature),
                  models_reasoning: resolveModelsReasoning(wm, wr),
                  use_rag: w.use_rag === true,
                  use_ltm: w.use_ltm === true,
                  use_diary_read: Boolean(w.use_diary_read),
                  use_diary_write: Boolean(w.use_diary_write),
                  use_portrait_read: Boolean(w.use_portrait_read),
                  use_portrait_write: Boolean(w.use_portrait_write),
                  is_trainable: typeof w.is_trainable === 'boolean' ? w.is_trainable : true,
                  reminder_prompt:
                    typeof w.reminder_prompt === 'string'
                      ? String(w.reminder_prompt).slice(0, MAX_AGENT_PROMPT_LENGTH)
                      : undefined,
                };
              })
            );
          }

          setMetaPrompts(sanitizeMetaPrompts(json.metaPrompts as Record<string, unknown>));

          if (json.label) {
            setConfigLabel(sanitizeConfigLabel(json.label));
          }

          setValidationError(null);
          setShowSuccessMessage(true);
          setTimeout(() => setShowSuccessMessage(false), NOTIFICATION_AUTO_HIDE_MS);
        } catch (error) {
          console.error('Failed to import configuration:', error);
          setValidationError('Failed to import configuration. Please check the JSON file format.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleCreateWorker = (mode: AgentWorker['mode']) => {
    const newWorker: AgentWorker = {
      id: uuid(),
      name: `Agent${String(workers.length + 1).padStart(3, '0')}`,
      description: 'Agent designed to provide an opinion or synthesis to the problem.',
      prompt: "You are an agent. Read the user's message and provide your contribution.",
      mode,
      model: 'gpt-4.1-mini',
      temperature: 0.7,
      ...(nltAdminMode ? defaultNltAdminToolExtras(true) : { use_rag: false, use_ltm: false }),
    };
    setWorkers([...workers, newWorker]);
  };

  const handleUpdateWorker = (id: string, updates: Partial<AgentWorker>) => {
    setWorkers(workers.map((w) => (w.id === id ? { ...w, ...updates } : w)));
  };

  const handleDeleteWorker = (id: string) => {
    setWorkers(workers.filter((w) => w.id !== id));
  };

  const handleDuplicateWorker = (worker: AgentWorker) => {
    const newWorker: AgentWorker = {
      ...worker,
      id: uuid(),
      name: sanitizeAgentName(worker.name + 'copy'),
    };
    setWorkers([...workers, newWorker]);
  };

  const handleApplyModelToAllAgents = () => {
    const nextModel = bulkModel || coordinator.model;
    setCoordinator((prev) => ({
      ...prev,
      model: nextModel,
      models_reasoning: resolveModelsReasoning(nextModel, prev.models_reasoning),
      is_trainable: resolveTrainableFlagForModelChange(prev.model, nextModel, prev.is_trainable),
    }));
    setWorkers(
      workers.map((w) => ({
        ...w,
        model: nextModel,
        models_reasoning: resolveModelsReasoning(nextModel, w.models_reasoning),
        is_trainable: resolveTrainableFlagForModelChange(w.model, nextModel, w.is_trainable),
      }))
    );
  };

  const setWorkersTrainableByMode = (mode: AgentWorker['mode'], value: boolean) => {
    setWorkers(
      workers.map((w) => {
        if (w.mode !== mode) return w;
        if (value && isBaseModelUnsupportedForFineTuning(w.model)) return { ...w, is_trainable: false };
        return { ...w, is_trainable: value };
      })
    );
  };

  useEffect(() => {
    if (isBaseModelUnsupportedForFineTuning(coordinator.model) && coordinator.is_trainable !== false) {
      setCoordinator((c) => ({ ...c, is_trainable: false }));
    }
  }, [coordinator.model, coordinator.is_trainable]);

  useEffect(() => {
    setWorkers((prev) => {
      let changed = false;
      const next = prev.map((w) => {
        if (isBaseModelUnsupportedForFineTuning(w.model) && w.is_trainable !== false) {
          changed = true;
          return { ...w, is_trainable: false };
        }
        return w;
      });
      return changed ? next : prev;
    });
  }, [workers]);

  return (
    <div className="agent-configurator-container">
      <div className="ac-header">
        <div className="ac-header-bar">
          <div className="ac-header-left">
            {onCancel && (
              <button className="ac-back-button" onClick={onCancel}>
                Back
              </button>
            )}
          </div>
          <div className="ac-header-actions">
            <button className="ac-btn ac-btn-icon-mobile" onClick={handleImportConfig} title="Import configuration from JSON file">
              Import
            </button>
            <button className="ac-btn ac-btn-icon-mobile" onClick={handleExportConfig} title="Export configuration to JSON file">
              Export
            </button>
            <button className="ac-btn" onClick={handleReset} title="Reset to last saved or defaults">
              Reset
            </button>
            <button className="ac-btn ac--primary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="ac-content">
        {validationError && (
          <div className="ac-inline-error" role="alert">
            {validationError}
          </div>
        )}
        {showSuccessMessage && <div className="ac-success-message">Configuration saved successfully!</div>}

        {patchReview && (
          <div className="ac-patch-review-banner" role="status">
            <div className="ac-patch-review-banner__text">
              <strong>NLT patch review</strong>
              {Object.keys(patchReview.fieldDecisions).length === 0 ? (
                <span> — no config changes were detected.</span>
              ) : (
                <span>
                  {' '}
                  — changes are highlighted next to each field (red = old, green = new). Reject fields you do not want;
                  untouched fields apply when you click Apply changes.
                </span>
              )}
            </div>
            {Object.keys(patchReview.fieldDecisions).length > 0 && !patchReview.hideBulkActions && (
              <div className="ac-patch-review-banner__actions">
                <button
                  type="button"
                  className="ac-btn ac--primary"
                  onClick={() => patchReview.onApplyAll?.()}
                  disabled={patchReview.applying}
                >
                  {patchReview.applying ? 'Applying…' : 'Apply changes'}
                </button>
                <button
                  type="button"
                  className="ac-btn"
                  onClick={() => patchReview.onRejectAll?.()}
                  disabled={patchReview.applying}
                >
                  Reject all
                </button>
              </div>
            )}
          </div>
        )}

        <div
          className="ac-card ac-card--bulk-model"
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            background: '#FFFFFF',
            borderColor: '#E3F0FB',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto auto',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0A2A47' }}>Apply model to all agents</div>
              <div style={{ marginTop: 2, fontSize: 12, color: '#5B7893' }}>
                Pick one model and apply it to coordinator and all workers.
              </div>
            </div>
            <select
              className="ac-bulk-model-select"
              value={bulkModel}
              onChange={(e) => setBulkModel(e.target.value)}
              style={{ minWidth: 240 }}
            >
              {availableModels.map((model) => (
                <option key={model} value={model}>
                  {formatConfiguratorModelLabel(model, modelLabels)}
                </option>
              ))}
            </select>
            <button type="button" className="ac-btn ac--primary" onClick={handleApplyModelToAllAgents}>
              Apply to all
            </button>
          </div>
        </div>

        {!hideApproach && (
          <PatchFieldWrap path="approach" patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#5B7893', minWidth: '70px' }}>Approach:</span>
            <div
              style={{
                display: 'inline-flex',
                background: '#F5F9FE',
                borderRadius: '999px',
                padding: '3px',
                gap: '4px',
                border: '1px solid var(--brand-primary, #FF8300)',
              }}
            >
              <button
                type="button"
                disabled={isPatchFieldLocked('approach')}
                onClick={() => setApproach('BASELINE')}
                style={{
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: approach === 'BASELINE' ? 'var(--brand-primary, #FF8300)' : 'transparent',
                  color: approach === 'BASELINE' ? '#FFFFFF' : 'var(--brand-primary, #FF8300)',
                  boxShadow: approach === 'BASELINE' ? '0 0 0 1px rgba(10, 42, 71, 0.02)' : 'none',
                }}
              >
                BASELINE
              </button>
              <button
                type="button"
                disabled={isPatchFieldLocked('approach')}
                onClick={() => setApproach('MULTISTAGE-PACE')}
                style={{
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: approach === 'MULTISTAGE-PACE' ? 'var(--brand-primary, #FF8300)' : 'transparent',
                  color: approach === 'MULTISTAGE-PACE' ? '#FFFFFF' : 'var(--brand-primary, #FF8300)',
                  boxShadow: approach === 'MULTISTAGE-PACE' ? '0 0 0 1px rgba(10, 42, 71, 0.02)' : 'none',
                }}
              >
                MULTISTAGE-PACE
              </button>
            </div>
          </div>
          </PatchFieldWrap>
        )}

        {!hideReminderPromptPosition && (
          <PatchFieldWrap
            path="reminder_prompt_position"
            patchReview={patchReview}
            patchFieldDiffMap={patchFieldDiffMap}
          >
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#5B7893', minWidth: '70px' }}>
              Reminder position
            </span>
            <div
              style={{
                display: 'inline-flex',
                background: '#F5F9FE',
                borderRadius: '999px',
                padding: '3px',
                gap: '4px',
                border: '1px solid #C6E0F7',
              }}
            >
              <button
                type="button"
                disabled={isPatchFieldLocked('reminder_prompt_position')}
                onClick={() => setReminderPromptPosition('before')}
                style={{
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: reminderPromptPosition === 'before' ? '#4A6683' : 'transparent',
                  color: reminderPromptPosition === 'before' ? '#FFFFFF' : '#4A6683',
                }}
              >
                Before
              </button>
              <button
                type="button"
                disabled={isPatchFieldLocked('reminder_prompt_position')}
                onClick={() => setReminderPromptPosition('after')}
                style={{
                  padding: '6px 16px',
                  fontSize: '13px',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: reminderPromptPosition === 'after' ? '#4A6683' : 'transparent',
                  color: reminderPromptPosition === 'after' ? '#FFFFFF' : '#4A6683',
                }}
              >
                After
              </button>
            </div>
            <span style={{ fontSize: '12px', color: '#7F9AB3' }} title="Optimization config field: reminder_prompt_position">
              Where reminder text is placed relative to the main agent instructions.
            </span>
          </div>
          </PatchFieldWrap>
        )}

        <div className="ac-config-name-section">
          <div className="ac-label-input-container">
            <div className="ac-label-input-wrapper">
              <input
                type="text"
                placeholder="Configuration name (optional)"
                value={configLabel}
                onChange={(e) => setConfigLabel(sanitizeConfigLabel(e.target.value))}
                className="ac-label-input"
                maxLength={MAX_CONFIG_LABEL_LENGTH}
              />
              <div className="ac-label-input-hint">
                {configLabel.length}/{MAX_CONFIG_LABEL_LENGTH}
              </div>
            </div>
          </div>
        </div>

        <div className="ac-flow">
          <div className="ac-columns">
            <div className="ac-col-left">
              <AcCollapsibleSection
                id="coordinator"
                title="Agent Coordinator"
                subtitle="Coordinates workers and responds to the user"
                collapsed={isSectionCollapsed('coordinator')}
                onToggle={() => toggleSection('coordinator')}
              >
                <div className="ac-grid ac--two">
                  <PatchFieldWrap
                    path="coordinator.name"
                    patchReview={patchReview}
                    patchFieldDiffMap={patchFieldDiffMap}
                  >
                    <div className={`ac-field${isCoordinatorNameDuplicated ? ' ac-field--error' : ''}`} style={{ gridColumn: '1 / -1' }}>
                      <label>Name</label>
                      <input
                        type="text"
                        placeholder="Coordinator name..."
                        value={coordinator.name}
                        maxLength={MAX_NAME_LENGTH}
                        pattern={AGENT_NAME_REGEX.source}
                        readOnly={isPatchFieldLocked('coordinator.name')}
                        onChange={(e) => setCoordinator({ ...coordinator, name: sanitizeAgentName(e.target.value) })}
                      />
                      {isCoordinatorNameDuplicated && (
                        <div className="ac-field-error-hint">This name is duplicated. Use a unique agent name.</div>
                      )}
                      <FieldCharCount length={coordinator.name.length} max={MAX_NAME_LENGTH} />
                    </div>
                  </PatchFieldWrap>

                  <PatchFieldWrap
                    path="coordinator.description"
                    patchReview={patchReview}
                    patchFieldDiffMap={patchFieldDiffMap}
                  >
                    <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Description</label>
                      <textarea
                        placeholder="Brief description..."
                        value={coordinator.description}
                        maxLength={MAX_AGENT_DESCRIPTION_LENGTH}
                        readOnly={isPatchFieldLocked('coordinator.description')}
                        onChange={(e) =>
                          setCoordinator({
                            ...coordinator,
                            description: e.target.value.slice(0, MAX_AGENT_DESCRIPTION_LENGTH),
                          })
                        }
                      />
                      <FieldCharCount length={coordinator.description.length} max={MAX_AGENT_DESCRIPTION_LENGTH} />
                    </div>
                  </PatchFieldWrap>

                  <PatchFieldWrap
                    path="coordinator.prompt"
                    patchReview={patchReview}
                    patchFieldDiffMap={patchFieldDiffMap}
                  >
                    <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Prompt</label>
                      <textarea
                        placeholder="Coordinator instructions..."
                        value={coordinator.prompt}
                        maxLength={MAX_COORDINATOR_PROMPT_LENGTH}
                        readOnly={isPatchFieldLocked('coordinator.prompt')}
                        onChange={(e) =>
                          setCoordinator({
                            ...coordinator,
                            prompt: e.target.value.slice(0, MAX_COORDINATOR_PROMPT_LENGTH),
                          })
                        }
                      />
                      <FieldCharCount length={coordinator.prompt.length} max={MAX_COORDINATOR_PROMPT_LENGTH} />
                    </div>
                  </PatchFieldWrap>

                  <PatchFieldWrap
                    path="coordinator.reminder_prompt"
                    patchReview={patchReview}
                    patchFieldDiffMap={patchFieldDiffMap}
                  >
                    <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Reminder prompt</label>
                      <textarea
                        placeholder={"e.g. Start your reply with the phrase '…'"}
                        value={coordinator.reminder_prompt ?? ''}
                        maxLength={MAX_AGENT_PROMPT_LENGTH}
                        readOnly={isPatchFieldLocked('coordinator.reminder_prompt')}
                        onChange={(e) =>
                          setCoordinator({
                            ...coordinator,
                            reminder_prompt: e.target.value.slice(0, MAX_AGENT_PROMPT_LENGTH) || undefined,
                          })
                        }
                      />
                      <FieldCharCount
                        length={(coordinator.reminder_prompt ?? '').length}
                        max={MAX_AGENT_PROMPT_LENGTH}
                      />
                    </div>
                  </PatchFieldWrap>

                  <PatchFieldWrap
                    path="coordinator.model"
                    patchReview={patchReview}
                    patchFieldDiffMap={patchFieldDiffMap}
                  >
                  <div className="ac-field">
                    <label>Model</label>
                    <select
                      value={coordinator.model}
                      disabled={isPatchFieldLocked('coordinator.model')}
                      onChange={(e) => {
                        const m = e.target.value;
                        setCoordinator({
                          ...coordinator,
                          model: m,
                          models_reasoning: resolveModelsReasoning(m, coordinator.models_reasoning),
                          is_trainable: resolveTrainableFlagForModelChange(
                            coordinator.model,
                            m,
                            coordinator.is_trainable
                          ),
                        });
                      }}
                    >
                      {availableModels.map((model) => (
                        <option key={model} value={model}>{formatConfiguratorModelLabel(model, modelLabels)}</option>
                      ))}
                    </select>
                  </div>
                  </PatchFieldWrap>

                  {(() => {
                    const spec = getGpt5ModelSpec(coordinator.model);
                    if (!spec) return null;
                    const mr =
                      resolveModelsReasoning(coordinator.model, coordinator.models_reasoning) ?? spec.defaultReasoning;
                    return (
                      <PatchFieldWrap
                        path="coordinator.models_reasoning"
                        patchReview={patchReview}
                        patchFieldDiffMap={patchFieldDiffMap}
                      >
                      <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                        <label>Reasoning</label>
                        <p className="ac-gpt5-hint">{spec.hint}</p>
                        <select
                          value={mr}
                          disabled={isPatchFieldLocked('coordinator.models_reasoning')}
                          onChange={(e) =>
                            setCoordinator({ ...coordinator, models_reasoning: e.target.value })
                          }
                        >
                          {spec.reasoningOptions.map((opt) => (
                            <option key={opt} value={opt}>
                              {reasoningOptionLabel(opt, spec.defaultReasoning)}
                            </option>
                          ))}
                        </select>
                      </div>
                      </PatchFieldWrap>
                    );
                  })()}

                  {(() => {
                    const coordMr = resolveModelsReasoning(coordinator.model, coordinator.models_reasoning);
                    const tempActive = shouldShowTemperature(coordinator.model, coordMr);
                    const inactiveHint = getTemperatureInactiveHint(coordinator.model, coordMr);
                    if (!getGpt5ModelSpec(coordinator.model)) {
                      return (
                        <PatchFieldWrap
                          path="coordinator.temperature"
                          patchReview={patchReview}
                          patchFieldDiffMap={patchFieldDiffMap}
                        >
                        <div className="ac-field ac-temperature-field">
                          <label>Temperature</label>
                          <div className="ac-range">
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={coordinator.temperature}
                              disabled={isPatchFieldLocked('coordinator.temperature')}
                              onChange={(e) =>
                                setCoordinator({ ...coordinator, temperature: parseFloat(e.target.value) })
                              }
                            />
                            <div className="ac-value">
                              <span>{coordinator.temperature.toFixed(1)}</span>
                            </div>
                          </div>
                        </div>
                        </PatchFieldWrap>
                      );
                    }
                    return (
                      <PatchFieldWrap
                        path="coordinator.temperature"
                        patchReview={patchReview}
                        patchFieldDiffMap={patchFieldDiffMap}
                      >
                      <div
                        className={`ac-field ac-temperature-field${tempActive ? '' : ' ac-temperature-field--inactive'}`}
                      >
                        <label>
                          Temperature
                          {!tempActive ? (
                            <span className="ac-temperature-field__badge">Not active</span>
                          ) : null}
                        </label>
                        {!tempActive && inactiveHint ? (
                          <p className="ac-temperature-field__hint">{inactiveHint}</p>
                        ) : null}
                        <div className="ac-range">
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={coordinator.temperature}
                            disabled={!tempActive || isPatchFieldLocked('coordinator.temperature')}
                            onChange={(e) =>
                              setCoordinator({ ...coordinator, temperature: parseFloat(e.target.value) })
                            }
                          />
                          <div className="ac-value">
                            <span>{coordinator.temperature.toFixed(1)}</span>
                          </div>
                        </div>
                      </div>
                      </PatchFieldWrap>
                    );
                  })()}

                  {nltAdminMode ? (
                    <NltAdminToolsFieldGroup
                      agent={coordinator}
                      onToggle={(field) => setCoordinator(applyNltAdminToolToggle(coordinator, field))}
                    />
                  ) : null}

                  {!nltAdminMode ? (
                  <>
                  {coordinatorToolDiffs.length > 0 && (
                    <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                      <PatchDiffList
                        diffs={coordinatorToolDiffs}
                        patchReview={patchReview}
                      />
                    </div>
                  )}
                  <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}>
                      Tools for Coordinator
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 12,
                          fontSize: 14,
                          color: '#4A6683',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const next = !coordinator.use_rag;
                          setCoordinator({
                            ...coordinator,
                            use_rag: next,
                            ...(next ? {} : { rag_doc_ids: undefined }),
                          });
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '48px',
                            height: '24px',
                            borderRadius: '12px',
                            background: coordinator.use_rag ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                            transition: 'background 0.2s ease',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: coordinator.use_rag ? '26px' : '2px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: '#FFFFFF',
                            boxShadow: '0 0 0 1px #E3F0FB',
                            transition: 'left 0.2s ease',
                          }} />
                        </div>
                        <span>Use RAG</span>
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 12,
                          fontSize: 14,
                          color: '#4A6683',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setCoordinator({ ...coordinator, use_ltm: !coordinator.use_ltm });
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '48px',
                            height: '24px',
                            borderRadius: '12px',
                            background: coordinator.use_ltm ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                            transition: 'background 0.2s ease',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: coordinator.use_ltm ? '26px' : '2px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: '#FFFFFF',
                            boxShadow: '0 0 0 1px #E3F0FB',
                            transition: 'left 0.2s ease',
                          }} />
                        </div>
                        <span>Use LTM</span>
                      </div>
                    </div>
                  </div>

                  <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}>
                      Diary & portrait
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      <AcBoolToggleRow
                        label="Diary read"
                        on={Boolean(coordinator.use_diary_read)}
                        onToggle={() =>
                          setCoordinator({ ...coordinator, use_diary_read: !coordinator.use_diary_read })
                        }
                      />
                      <AcBoolToggleRow
                        label="Diary write"
                        on={Boolean(coordinator.use_diary_write)}
                        onToggle={() =>
                          setCoordinator({ ...coordinator, use_diary_write: !coordinator.use_diary_write })
                        }
                      />
                      <AcBoolToggleRow
                        label="Portrait read"
                        on={Boolean(coordinator.use_portrait_read)}
                        onToggle={() =>
                          setCoordinator({ ...coordinator, use_portrait_read: !coordinator.use_portrait_read })
                        }
                      />
                      <AcBoolToggleRow
                        label="Portrait write"
                        on={Boolean(coordinator.use_portrait_write)}
                        onToggle={() =>
                          setCoordinator({ ...coordinator, use_portrait_write: !coordinator.use_portrait_write })
                        }
                      />
                    </div>
                  </div>

                  <PatchFieldWrap
                    path="coordinator.is_trainable"
                    patchReview={patchReview}
                    patchFieldDiffMap={patchFieldDiffMap}
                  >
                  <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}>
                      Trainable for fine-tuning
                    </label>
                    {(() => {
                      const ftBlocked = isBaseModelUnsupportedForFineTuning(coordinator.model);
                      const trainableLocked = isPatchFieldLocked('coordinator.is_trainable');
                      const on = coordinator.is_trainable !== false && !ftBlocked;
                      return (
                        <>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 12,
                              fontSize: 14,
                              color: '#4A6683',
                              cursor: ftBlocked || trainableLocked ? 'not-allowed' : 'pointer',
                              userSelect: 'none',
                              opacity: ftBlocked || trainableLocked ? 0.65 : 1,
                            }}
                            title={
                              ftBlocked
                                ? 'GPT-5 family models cannot be fine-tuned; use another base model for training jobs.'
                                : undefined
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (ftBlocked || trainableLocked) return;
                              setCoordinator({ ...coordinator, is_trainable: !(coordinator.is_trainable !== false) });
                            }}
                          >
                            <div
                              style={{
                                position: 'relative',
                                width: '48px',
                                height: '24px',
                                borderRadius: '12px',
                                background: on ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                                transition: 'background 0.2s ease',
                                cursor: ftBlocked ? 'not-allowed' : 'pointer',
                                flexShrink: 0,
                              }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '2px',
                                  left: on ? '26px' : '2px',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  background: '#FFFFFF',
                                  boxShadow: '0 0 0 1px #E3F0FB',
                                  transition: 'left 0.2s ease',
                                }}
                              />
                            </div>
                            <span>Include coordinator in Training Job</span>
                          </div>
                          {ftBlocked && (
                            <p className="ac-gpt5-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                              Fine-tuning is not available for this model (GPT-5 family).
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  </PatchFieldWrap>
                  </>
                  ) : null}
                </div>
              </AcCollapsibleSection>

              <section className="ac-workers">
                <AcCollapsibleSection
                  id="syncWorkers"
                  title="Worker Agents"
                  subtitle={`Agents: ${syncWorkers.length}`}
                  collapsed={isSectionCollapsed('syncWorkers')}
                  onToggle={() => toggleSection('syncWorkers')}
                  toolbarPlacement="below"
                  toolbar={
                    <>
                      {!nltAdminMode
                        ? (() => {
                            const allIncluded =
                              syncWorkers.length > 0 && syncWorkers.every((w) => w.is_trainable !== false);
                            return (
                              <div
                                className="ac-toggle-group"
                                role="group"
                                aria-label="Synchronous agents training"
                                style={{
                                  display: 'inline-flex',
                                  borderRadius: 8,
                                  border: '1px solid #E3F0FB',
                                  overflow: 'hidden',
                                  background: '#F5F9FE',
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => setWorkersTrainableByMode('synchronous', false)}
                                  title="Exclude all synchronous agents from Training Job"
                                  style={{
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    background: !allIncluded ? '#FFFFFF' : 'transparent',
                                    color: !allIncluded ? '#4A6683' : '#5B7893',
                                    boxShadow: !allIncluded ? '0 1px 2px rgba(10, 42, 71, 0.06)' : 'none',
                                  }}
                                >
                                  Exclude all for training job
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setWorkersTrainableByMode('synchronous', true)}
                                  title="Include all synchronous agents in Training Job"
                                  style={{
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    background: allIncluded ? '#FFFFFF' : 'transparent',
                                    color: allIncluded ? '#4A6683' : '#5B7893',
                                    boxShadow: allIncluded ? '0 1px 2px rgba(10, 42, 71, 0.06)' : 'none',
                                  }}
                                >
                                  Include all for training job
                                </button>
                              </div>
                            );
                          })()
                        : null}
                      <button className="ac-btn ac--primary" onClick={() => handleCreateWorker('synchronous')}>
                        Create agent
                      </button>
                    </>
                  }
                >
                  <div className="ac-workers-grid">
                    {syncWorkers.map((worker) => (
                      <WorkerCard
                        key={worker.id}
                        worker={worker}
                        onUpdate={handleUpdateWorker}
                        onDelete={handleDeleteWorker}
                        onDuplicate={handleDuplicateWorker}
                        availableModels={availableModels}
                        modelLabels={modelLabels}
                        isNameDuplicated={duplicateAgentNameKeys.has(String(worker.name || '').trim().toLowerCase())}
                        nltAdminMode={nltAdminMode}
                        patchReview={patchReview}
                        patchFieldDiffMap={patchFieldDiffMap}
                        isPatchFieldLocked={isPatchFieldLocked}
                      />
                    ))}
                  </div>
                </AcCollapsibleSection>
              </section>
            </div>

            <div className="ac-col-right">
              <AcCollapsibleSection
                id="asyncWorkers"
                title="Asynchronous Agents"
                subtitle={`Agents: ${asyncWorkers.length}`}
                collapsed={isSectionCollapsed('asyncWorkers')}
                onToggle={() => toggleSection('asyncWorkers')}
                toolbarPlacement="below"
                toolbar={
                  <>
                    {!nltAdminMode
                      ? (() => {
                          const allIncludedAsync =
                            asyncWorkers.length > 0 && asyncWorkers.every((w) => w.is_trainable !== false);
                          return (
                            <div
                              className="ac-toggle-group"
                              role="group"
                              aria-label="Asynchronous agents training"
                              style={{
                                display: 'inline-flex',
                                borderRadius: 8,
                                border: '1px solid #E3F0FB',
                                overflow: 'hidden',
                                background: '#F5F9FE',
                              }}
                            >
                              <button
                                type="button"
                                onClick={() => setWorkersTrainableByMode('asynchronous', false)}
                                title="Exclude all asynchronous agents from Training Job"
                                style={{
                                  padding: '6px 12px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  border: 'none',
                                  cursor: 'pointer',
                                  background: !allIncludedAsync ? '#FFFFFF' : 'transparent',
                                  color: !allIncludedAsync ? '#4A6683' : '#5B7893',
                                  boxShadow: !allIncludedAsync ? '0 1px 2px rgba(10, 42, 71, 0.06)' : 'none',
                                }}
                              >
                                Exclude all for training job
                              </button>
                              <button
                                type="button"
                                onClick={() => setWorkersTrainableByMode('asynchronous', true)}
                                title="Include all asynchronous agents in Training Job"
                                style={{
                                  padding: '6px 12px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  border: 'none',
                                  cursor: 'pointer',
                                  background: allIncludedAsync ? '#FFFFFF' : 'transparent',
                                  color: allIncludedAsync ? '#4A6683' : '#5B7893',
                                  boxShadow: allIncludedAsync ? '0 1px 2px rgba(10, 42, 71, 0.06)' : 'none',
                                }}
                              >
                                Include all for training job
                              </button>
                            </div>
                          );
                        })()
                      : null}
                    <button className="ac-btn ac--primary" onClick={() => handleCreateWorker('asynchronous')}>
                      Create agent
                    </button>
                  </>
                }
              >
                <div className="ac-workers-grid">
                  {asyncWorkers.map((worker) => (
                    <WorkerCard
                      key={worker.id}
                      worker={worker}
                      onUpdate={handleUpdateWorker}
                      onDelete={handleDeleteWorker}
                      onDuplicate={handleDuplicateWorker}
                      availableModels={availableModels}
                      modelLabels={modelLabels}
                      isNameDuplicated={duplicateAgentNameKeys.has(String(worker.name || '').trim().toLowerCase())}
                      nltAdminMode={nltAdminMode}
                      patchReview={patchReview}
                      patchFieldDiffMap={patchFieldDiffMap}
                      isPatchFieldLocked={isPatchFieldLocked}
                    />
                  ))}
                </div>
              </AcCollapsibleSection>
            </div>
          </div>

          <AcCollapsibleSection
            id="metaPrompts"
            style={{ marginTop: 24 }}
            title="Meta Prompts"
            subtitle="System instructions"
            collapsed={isSectionCollapsed('metaPrompts')}
            onToggle={() => toggleSection('metaPrompts')}
          >
            <div className="ac-grid" style={{ gridTemplateColumns: '1fr' }}>
              {(
                [
                  { key: 'CORE_PROMPT', label: 'CORE_PROMPT', max: MAX_CORE_PROMPT_LENGTH, minHeight: 180 },
                  { key: 'MASTER_SUFFIX', label: 'MASTER_SUFFIX', max: MAX_META_PROMPT_LENGTH },
                  { key: 'ASYNC_SUFFIX', label: 'ASYNC_SUFFIX', max: MAX_META_PROMPT_LENGTH },
                  { key: 'SYNC_EXTRA', label: 'SYNC_EXTRA', max: MAX_META_PROMPT_LENGTH },
                  { key: 'RAG_PROMPT', label: 'RAG_PROMPT', max: MAX_META_PROMPT_LENGTH },
                  {
                    key: 'FINAL_STEP_PROMPT',
                    label: (
                      <>
                        FINAL STEP
                        <span style={{ fontWeight: 400, color: '#5B7893', fontSize: 12, marginLeft: 8 }}>
                          (FINAL_STEP_PROMPT)
                        </span>
                      </>
                    ),
                    max: MAX_META_PROMPT_LENGTH,
                  },
                  { key: 'LTM_PROMPT', label: 'LTM_PROMPT', max: MAX_META_PROMPT_LENGTH },
                  { key: 'MODERATION_PROMPT', label: 'Moderation prompt', max: MAX_META_PROMPT_LENGTH },
                  { key: 'DIARY_WRITE_PROMPT', label: 'Diary write prompt', max: MAX_META_PROMPT_LENGTH },
                  { key: 'DIARY_READ_PROMPT', label: 'Diary read prompt', max: MAX_META_PROMPT_LENGTH },
                  { key: 'PORTRAIT_READ_PROMPT', label: 'Portrait read prompt', max: MAX_META_PROMPT_LENGTH },
                  { key: 'PORTRAIT_WRITE_PROMPT', label: 'Portrait write prompt', max: MAX_META_PROMPT_LENGTH },
                ] as const
              ).map((item) => {
                const { key, label, max } = item;
                const minHeight = 'minHeight' in item ? item.minHeight : 120;
                return (
                <PatchFieldWrap
                  key={key}
                  path={`metaPrompts.${key}`}
                  patchReview={patchReview}
                  patchFieldDiffMap={patchFieldDiffMap}
                >
                  <div className="ac-field">
                    <label>{label}</label>
                    <textarea
                      value={metaPrompts[key] ?? ''}
                      maxLength={max}
                      readOnly={isPatchFieldLocked(`metaPrompts.${key}`)}
                      onChange={(e) =>
                        setMetaPrompts({ ...metaPrompts, [key]: e.target.value.slice(0, max) })
                      }
                      style={{ minHeight }}
                    />
                    <FieldCharCount length={(metaPrompts[key] ?? '').length} max={max} />
                  </div>
                </PatchFieldWrap>
                );
              })}
            </div>
          </AcCollapsibleSection>
        </div>
      </div>
    </div>
  );
};

function AcBoolToggleRow({
  label,
  description,
  on,
  onToggle,
}: {
  label: string;
  description?: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        fontSize: 14,
        color: '#4A6683',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '48px',
          height: '24px',
          borderRadius: '12px',
          background: on ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
          transition: 'background 0.2s ease',
          cursor: 'pointer',
          flexShrink: 0,
          marginTop: description ? 2 : 0,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '2px',
            left: on ? '26px' : '2px',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0 0 0 1px #E3F0FB',
            transition: 'left 0.2s ease',
          }}
        />
      </div>
      <div style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        {description ? (
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#5B7893', lineHeight: 1.4 }}>{description}</p>
        ) : null}
      </div>
    </div>
  );
}

function NltAdminToolsFieldGroup({
  agent,
  onToggle,
}: {
  agent: AgentCoordinatorState | AgentWorker;
  onToggle: (field: NltAdminAgentToolField) => void;
}) {
  return (
    <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
      <label
        style={{
          fontSize: '14px',
          fontWeight: 500,
          color: '#4A6683',
          marginBottom: '8px',
          display: 'block',
        }}
      >
        NLT tools
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {NLT_ADMIN_AGENT_TOOL_FIELDS.map((field) => {
          const ui = NLT_ADMIN_AGENT_TOOL_UI[field];
          return (
            <AcBoolToggleRow
              key={field}
              label={ui.label}
              description={ui.description}
              on={isNltAdminToolEnabled(agent, field)}
              onToggle={() => onToggle(field)}
            />
          );
        })}
      </div>
    </div>
  );
}

function PatchFieldWrap({
  path,
  patchReview,
  patchFieldDiffMap,
  children,
}: {
  path: string;
  patchReview: AgentConfiguratorPatchReview | null;
  patchFieldDiffMap: Record<string, ConfigFieldDiff>;
  children: React.ReactNode;
}) {
  if (!patchReview) {
    return <>{children}</>;
  }
  const diff = patchFieldDiffMap[path];
  if (!diff) {
    return <>{children}</>;
  }
  const focused = patchReview.focusedPatchPath === path;
  return (
    <div
      className={['ac-field-patch-host', focused ? 'ac-field-patch-host--focused' : '']
        .filter(Boolean)
        .join(' ')}
      style={{ gridColumn: '1 / -1' }}
    >
      <AcFieldPatchDiff
        diff={diff}
        decision={patchReview.fieldDecisions[path] ?? 'pending'}
        onDecision={patchReview.onFieldDecision}
      >
        {children}
      </AcFieldPatchDiff>
    </div>
  );
}

function PatchDiffList({
  diffs,
  patchReview,
}: {
  diffs: ConfigFieldDiff[];
  patchReview: AgentConfiguratorPatchReview | null;
}) {
  if (!patchReview || diffs.length === 0) return null;
  return (
    <div className="ac-field-patch-list">
      {diffs.map((diff) => (
        <AcFieldPatchDiff
          key={diff.path}
          diff={diff}
          decision={patchReview.fieldDecisions[diff.path] ?? 'pending'}
          onDecision={patchReview.onFieldDecision}
        >
          <div className="ac-field-patch-list__placeholder" />
        </AcFieldPatchDiff>
      ))}
    </div>
  );
}

interface WorkerCardProps {
  worker: AgentWorker;
  onUpdate: (id: string, updates: Partial<AgentWorker>) => void;
  onDelete: (id: string) => void;
  onDuplicate: (worker: AgentWorker) => void;
  availableModels: string[];
  modelLabels?: Record<string, string>;
  isNameDuplicated: boolean;
  nltAdminMode?: boolean;
  patchReview?: AgentConfiguratorPatchReview | null;
  patchFieldDiffMap: Record<string, ConfigFieldDiff>;
  isPatchFieldLocked: (path: string) => boolean;
}

const WorkerCard: React.FC<WorkerCardProps> = ({
  worker,
  onUpdate,
  onDelete,
  onDuplicate,
  availableModels,
  modelLabels,
  isNameDuplicated,
  nltAdminMode = false,
  patchReview = null,
  patchFieldDiffMap,
  isPatchFieldLocked,
}) => {
  const wKey = workerPathKey(worker);
  const wp = (field: string) => `workers.${wKey}.${field}`;
  const workerToolDiffs = Object.values(patchFieldDiffMap).filter(
    (diff) => diff.path.startsWith(`workers.${wKey}.use_`)
  );
  const [collapsed, setCollapsed] = useState(false);
  const isAsync = worker.mode === 'asynchronous';
  const displayName = String(worker.name || '').trim() || 'Untitled agent';

  return (
    <AcCollapsibleSection
      className="ac-card ac-worker ac-worker-collapsible"
      titleVariant="plain"
      toolbarPlacement="inline"
      showToolbarWhenCollapsed
      title={
        <span className="ac-worker-collapsible__heading">
          <span className={`ac-chip ac-mode-chip ${isAsync ? 'ac--async' : 'ac--sync'}`}>{isAsync ? 'Async' : 'Sync'}</span>
          {collapsed ? (
            <span className="ac-worker-collapsible__name" title={displayName}>
              {displayName}
            </span>
          ) : null}
        </span>
      }
      subtitle={collapsed ? worker.model : undefined}
      collapsed={collapsed}
      onToggle={() => setCollapsed((prev) => !prev)}
      toolbar={
        <>
          <button
            type="button"
            className="ac-btn ac-w-duplicate"
            title="Duplicate"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(worker);
            }}
          >
            Duplicate
          </button>
          <button
            type="button"
            className="ac-btn ac--danger ac-w-remove"
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(worker.id);
            }}
          >
            Delete
          </button>
        </>
      }
    >
      <div className="ac-grid ac--two ac-w-content">
        <PatchFieldWrap path={wp('name')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
          <div className={`ac-field${isNameDuplicated ? ' ac-field--error' : ''}`} style={{ gridColumn: '1 / -1' }}>
            <label>Name</label>
            <input
              type="text"
              placeholder="Agent name (A-Z, a-z, 0-9)"
              value={worker.name}
              maxLength={MAX_NAME_LENGTH}
              pattern={AGENT_NAME_REGEX.source}
              readOnly={isPatchFieldLocked(wp('name'))}
              onChange={(e) => onUpdate(worker.id, { name: sanitizeAgentName(e.target.value) })}
            />
            {isNameDuplicated && (
              <div className="ac-field-error-hint">This name is duplicated. Use a unique agent name.</div>
            )}
            <FieldCharCount length={worker.name.length} max={MAX_NAME_LENGTH} />
          </div>
        </PatchFieldWrap>
        <PatchFieldWrap path={wp('description')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea
              placeholder="Brief description..."
              value={worker.description}
              maxLength={MAX_AGENT_DESCRIPTION_LENGTH}
              readOnly={isPatchFieldLocked(wp('description'))}
              onChange={(e) =>
                onUpdate(worker.id, { description: e.target.value.slice(0, MAX_AGENT_DESCRIPTION_LENGTH) })
              }
            />
            <FieldCharCount length={worker.description.length} max={MAX_AGENT_DESCRIPTION_LENGTH} />
          </div>
        </PatchFieldWrap>
        <PatchFieldWrap path={wp('prompt')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Prompt</label>
            <textarea
              placeholder="Instructions..."
              value={worker.prompt}
              maxLength={MAX_AGENT_PROMPT_LENGTH}
              readOnly={isPatchFieldLocked(wp('prompt'))}
              onChange={(e) => onUpdate(worker.id, { prompt: e.target.value.slice(0, MAX_AGENT_PROMPT_LENGTH) })}
            />
            <FieldCharCount length={worker.prompt.length} max={MAX_AGENT_PROMPT_LENGTH} />
          </div>
        </PatchFieldWrap>
        <PatchFieldWrap path={wp('reminder_prompt')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <label>Reminder prompt</label>
            <textarea
              placeholder={"e.g. Start your reply with the phrase '…'"}
              value={worker.reminder_prompt ?? ''}
              maxLength={MAX_AGENT_PROMPT_LENGTH}
              readOnly={isPatchFieldLocked(wp('reminder_prompt'))}
              onChange={(e) =>
                onUpdate(worker.id, {
                  reminder_prompt: e.target.value.slice(0, MAX_AGENT_PROMPT_LENGTH) || undefined,
                })
              }
            />
            <FieldCharCount length={(worker.reminder_prompt ?? '').length} max={MAX_AGENT_PROMPT_LENGTH} />
          </div>
        </PatchFieldWrap>
        <PatchFieldWrap path={wp('model')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
        <div className="ac-field">
          <label>Model</label>
          <select
            value={worker.model}
            disabled={isPatchFieldLocked(wp('model'))}
            onChange={(e) => {
              const m = e.target.value;
              onUpdate(worker.id, {
                model: m,
                models_reasoning: resolveModelsReasoning(m, worker.models_reasoning),
                is_trainable: resolveTrainableFlagForModelChange(
                  worker.model,
                  m,
                  worker.is_trainable
                ),
              });
            }}
          >
            {availableModels.map((model) => (
              <option key={model} value={model}>{formatConfiguratorModelLabel(model, modelLabels)}</option>
            ))}
          </select>
        </div>
        </PatchFieldWrap>
        {(() => {
          const spec = getGpt5ModelSpec(worker.model);
          if (!spec) return null;
          const mr = resolveModelsReasoning(worker.model, worker.models_reasoning) ?? spec.defaultReasoning;
          return (
            <PatchFieldWrap path={wp('models_reasoning')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
            <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
              <label>Reasoning</label>
              <p className="ac-gpt5-hint">{spec.hint}</p>
              <select
                value={mr}
                disabled={isPatchFieldLocked(wp('models_reasoning'))}
                onChange={(e) => onUpdate(worker.id, { models_reasoning: e.target.value })}
              >
                {spec.reasoningOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {reasoningOptionLabel(opt, spec.defaultReasoning)}
                  </option>
                ))}
              </select>
            </div>
            </PatchFieldWrap>
          );
        })()}
        {(() => {
          const wMr = resolveModelsReasoning(worker.model, worker.models_reasoning);
          const tempActive = shouldShowTemperature(worker.model, wMr);
          const inactiveHint = getTemperatureInactiveHint(worker.model, wMr);
          if (!getGpt5ModelSpec(worker.model)) {
            return (
              <PatchFieldWrap path={wp('temperature')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
              <div className="ac-field ac-temperature-field">
                <label>Temperature</label>
                <div className="ac-range">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={worker.temperature}
                    disabled={isPatchFieldLocked(wp('temperature'))}
                    onChange={(e) => onUpdate(worker.id, { temperature: parseFloat(e.target.value) })}
                  />
                  <div className="ac-value">
                    <span>{worker.temperature.toFixed(1)}</span>
                  </div>
                </div>
              </div>
              </PatchFieldWrap>
            );
          }
          return (
            <PatchFieldWrap path={wp('temperature')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
            <div
              className={`ac-field ac-temperature-field${tempActive ? '' : ' ac-temperature-field--inactive'}`}
            >
              <label>
                Temperature
                {!tempActive ? <span className="ac-temperature-field__badge">Not active</span> : null}
              </label>
              {!tempActive && inactiveHint ? (
                <p className="ac-temperature-field__hint">{inactiveHint}</p>
              ) : null}
              <div className="ac-range">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={worker.temperature}
                  disabled={!tempActive || isPatchFieldLocked(wp('temperature'))}
                  onChange={(e) => onUpdate(worker.id, { temperature: parseFloat(e.target.value) })}
                />
                <div className="ac-value">
                  <span>{worker.temperature.toFixed(1)}</span>
                </div>
              </div>
            </div>
            </PatchFieldWrap>
          );
        })()}
        {nltAdminMode ? (
          <NltAdminToolsFieldGroup
            agent={worker}
            onToggle={(field) => onUpdate(worker.id, toggleNltAdminTool(worker, field))}
          />
        ) : null}

        {!nltAdminMode ? (
        <>
        {workerToolDiffs.length > 0 && (
          <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
            <PatchDiffList diffs={workerToolDiffs} patchReview={patchReview} />
          </div>
        )}
        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}>
            Tools for this agent
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 14,
                color: '#4A6683',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const next = !worker.use_rag;
                onUpdate(worker.id, {
                  use_rag: next,
                  ...(next ? {} : { rag_doc_ids: undefined }),
                });
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '48px',
                  height: '24px',
                  borderRadius: '12px',
                  background: worker.use_rag ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                  transition: 'background 0.2s ease',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: worker.use_rag ? '26px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#FFFFFF',
                  boxShadow: '0 0 0 1px #E3F0FB',
                  transition: 'left 0.2s ease',
                }} />
              </div>
              <span>Use RAG</span>
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                fontSize: 14,
                color: '#4A6683',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onUpdate(worker.id, { use_ltm: !worker.use_ltm });
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '48px',
                  height: '24px',
                  borderRadius: '12px',
                  background: worker.use_ltm ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                  transition: 'background 0.2s ease',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: worker.use_ltm ? '26px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#FFFFFF',
                  boxShadow: '0 0 0 1px #E3F0FB',
                  transition: 'left 0.2s ease',
                }} />
              </div>
              <span>Use LTM</span>
            </div>
          </div>
        </div>
        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}>
            Diary & portrait
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <AcBoolToggleRow
              label="Diary read"
              on={Boolean(worker.use_diary_read)}
              onToggle={() => onUpdate(worker.id, { use_diary_read: !worker.use_diary_read })}
            />
            <AcBoolToggleRow
              label="Diary write"
              on={Boolean(worker.use_diary_write)}
              onToggle={() => onUpdate(worker.id, { use_diary_write: !worker.use_diary_write })}
            />
            <AcBoolToggleRow
              label="Portrait read"
              on={Boolean(worker.use_portrait_read)}
              onToggle={() => onUpdate(worker.id, { use_portrait_read: !worker.use_portrait_read })}
            />
            <AcBoolToggleRow
              label="Portrait write"
              on={Boolean(worker.use_portrait_write)}
              onToggle={() => onUpdate(worker.id, { use_portrait_write: !worker.use_portrait_write })}
            />
          </div>
        </div>
        <PatchFieldWrap path={wp('is_trainable')} patchReview={patchReview} patchFieldDiffMap={patchFieldDiffMap}>
        <div className="ac-field" style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, color: '#4A6683', marginBottom: '8px', display: 'block' }}>
            Trainable for fine-tuning
          </label>
          {(() => {
            const ftBlocked = isBaseModelUnsupportedForFineTuning(worker.model);
            const trainableLocked = isPatchFieldLocked(wp('is_trainable'));
            const on = worker.is_trainable !== false && !ftBlocked;
            return (
              <>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 14,
                    color: '#4A6683',
                    cursor: ftBlocked || trainableLocked ? 'not-allowed' : 'pointer',
                    userSelect: 'none',
                    opacity: ftBlocked || trainableLocked ? 0.65 : 1,
                  }}
                  title={
                    ftBlocked
                      ? 'GPT-5 family models cannot be fine-tuned; use another base model for training jobs.'
                      : undefined
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (ftBlocked || trainableLocked) return;
                    onUpdate(worker.id, { is_trainable: !(worker.is_trainable !== false) });
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: '48px',
                      height: '24px',
                      borderRadius: '12px',
                      background: on ? 'var(--brand-primary, #FF8300)' : '#C6E0F7',
                      transition: 'background 0.2s ease',
                      cursor: ftBlocked ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '2px',
                        left: on ? '26px' : '2px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: '#FFFFFF',
                        boxShadow: '0 0 0 1px #E3F0FB',
                        transition: 'left 0.2s ease',
                      }}
                    />
                  </div>
                  <span>Include this agent in Training Job</span>
                </div>
                {ftBlocked && (
                  <p className="ac-gpt5-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                    Fine-tuning is not available for this model (GPT-5 family).
                  </p>
                )}
              </>
            );
          })()}
        </div>
        </PatchFieldWrap>
        </>
        ) : null}
      </div>
    </AcCollapsibleSection>
  );
};

export default AgentConfigurator;
